import React, { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { computeMapLayerPopoverPosition, type MapLayerPopoverAvoidRect } from '@/lib/mapLayerPopoverGeometry';

export interface MapLayerSelectorOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  icon?: ReactNode;
}

export interface MapLayerSelectorTrace {
  phase: 'document-pointerdown' | 'outside-dismiss' | 'backdrop-pointerdown' | 'menu-pointerdown' | 'option-pointerdown' | 'option-click' | 'on-change-start' | 'on-change-end';
  option?: string;
  target?: string;
  pointerType?: string;
  containment?: 'menu' | 'trigger' | 'outside';
}

interface MapLayerSelectorPopoverProps<T extends string> {
  value: T;
  options: readonly MapLayerSelectorOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  testId?: string;
  disabled?: boolean;
  onTrace?: (entry: MapLayerSelectorTrace) => void;
  /** Keeps map popovers inside the pointer-enabled dialog subtree when supplied. */
  portalContainer?: HTMLElement | null;
  /** Controlled open state for the map's one-transient-surface state machine. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Returns fixed-screen map-control rectangles that the menu must not cover. */
  getAvoidRects?: () => readonly MapLayerPopoverAvoidRect[];
  triggerClassName?: string;
  renderTrigger?: (state: { selected: MapLayerSelectorOption<T> | undefined; open: boolean }) => ReactNode;
}

function describeSafeTarget(target: EventTarget | null): string {
  if (!(target instanceof Element)) return 'unknown';
  const role = target.getAttribute('role');
  return role ? `${target.tagName.toLowerCase()}[${role}]` : target.tagName.toLowerCase();
}

/**
 * Compact application-owned map-layer picker.
 * The options are portaled to document.body to avoid clipping inside the mobile map sheet.
 */
export function MapLayerSelectorPopover<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  testId,
  disabled = false,
  onTrace,
  portalContainer,
  open: controlledOpen,
  onOpenChange,
  getAvoidRects,
  triggerClassName = '',
  renderTrigger,
}: MapLayerSelectorPopoverProps<T>) {
  const menuId = `map-layer-selector-${useId().replace(/[:]/g, '')}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState({ top: 12, left: 12, width: 260, maxHeight: 300, placement: 'below' as const });

  const selected = options.find(option => option.value === value);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback((next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [controlledOpen, onOpenChange]);

  const emitTrace = useCallback((entry: MapLayerSelectorTrace) => {
    onTrace?.(entry);
  }, [onTrace]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const visualViewport = window.visualViewport;
    setPosition(computeMapLayerPopoverPosition({
      trigger: rect,
      viewport: {
        top: visualViewport?.offsetTop ?? 0,
        left: visualViewport?.offsetLeft ?? 0,
        width: visualViewport?.width ?? window.innerWidth,
        height: visualViewport?.height ?? window.innerHeight,
      },
      optionCount: options.length,
      avoidRects: getAvoidRects?.(),
    }));
  }, [getAvoidRects, options.length]);

  const schedulePosition = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, [setOpen]);

  const choose = useCallback((next: T) => {
    emitTrace({ phase: 'on-change-start', option: next });
    onChange(next);
    emitTrace({ phase: 'on-change-end', option: next });
    close(true);
  }, [close, emitTrace, onChange]);

  const consumeOptionPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>, option: T) => {
    // Do not prevent default here. On Android Chrome, preventing a touch
    // pointer-down suppresses the following click, so the menu closes without
    // committing the option. The full-viewport shield remains mounted until
    // the click below commits and closes the menu.
    emitTrace({ phase: 'option-pointerdown', option, target: describeSafeTarget(event.target), pointerType: event.pointerType || 'unknown' });
    event.stopPropagation();
  }, [emitTrace]);

  const chooseFromClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, next: T) => {
    // Stop React/DOM bubbling before unmounting the portal so this real click
    // cannot activate a map-tool control underneath the selector.
    emitTrace({ phase: 'option-click', option: next, target: describeSafeTarget(event.target) });
    event.preventDefault();
    event.stopPropagation();
    choose(next);
  }, [choose, emitTrace]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedulePosition) : null;
    if (triggerRef.current) resizeObserver?.observe(triggerRef.current);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', schedulePosition);
    window.addEventListener('scroll', schedulePosition, true);
    window.visualViewport?.addEventListener('resize', schedulePosition);
    window.visualViewport?.addEventListener('scroll', schedulePosition);
    schedulePosition();
    const focusFrame = window.requestAnimationFrame(() => menuRef.current?.focus());
    return () => {
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      window.cancelAnimationFrame(focusFrame);
      resizeObserver?.disconnect();
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', schedulePosition);
      window.removeEventListener('scroll', schedulePosition, true);
      window.visualViewport?.removeEventListener('resize', schedulePosition);
      window.visualViewport?.removeEventListener('scroll', schedulePosition);
    };
  }, [close, open, schedulePosition]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      close(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
    } else if ((event.key === 'Enter' || event.key === ' ') && options[activeIndex]) {
      event.preventDefault();
      choose(options[activeIndex].value);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (open) close(false);
          else {
            setActiveIndex(Math.max(0, options.findIndex(option => option.value === value)));
            setOpen(true);
          }
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(event.key === 'ArrowDown' ? 0 : options.length - 1);
            setOpen(true);
          }
        }}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={`flex min-h-11 w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-800 shadow-sm transition-colors active:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:cursor-not-allowed disabled:opacity-60 ${triggerClassName}`}
      >
        {renderTrigger ? renderTrigger({ selected, open }) : <>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#002975]/10 text-[#002975]" aria-hidden="true"><Layers className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1 truncate">{selected?.label ?? ariaLabel}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </>}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          data-testid={testId ? `${testId}-portal-surface` : undefined}
          className="pointer-events-none fixed inset-0"
          style={{ zIndex: 10020 }}
        >
          {/* BUG-v294: Android trace proved the native document capture listener could
              close the portal before a visible option received its click. A single,
              explicit portal surface now owns only true outside presses. The inline
              stacking level keeps hit-testing above the z-[10000] mobile tools sheet. */}
          <div data-testid={testId ? `${testId}-backdrop` : undefined} className="pointer-events-auto absolute inset-0 touch-none" aria-hidden="true" onPointerDown={event => { emitTrace({ phase: 'backdrop-pointerdown', target: describeSafeTarget(event.target), pointerType: event.pointerType || 'unknown' }); event.preventDefault(); event.stopPropagation(); close(false); }} />
          <div
            ref={menuRef}
            id={menuId}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-activedescendant={`${menuId}-${activeIndex}`}
            data-testid={testId ? `${testId}-menu` : undefined}
            onKeyDown={handleMenuKeyDown}
            onPointerDown={event => { emitTrace({ phase: 'menu-pointerdown', target: describeSafeTarget(event.target), pointerType: event.pointerType || 'unknown' }); event.stopPropagation(); }}
            style={{ position: 'fixed', top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight, zIndex: 1, pointerEvents: 'auto' }}
            className="overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-1 shadow-xl shadow-slate-950/20 touch-pan-y"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  id={`${menuId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onPointerDown={event => consumeOptionPointerDown(event, option.value)}
                  onClick={event => chooseFromClick(event, option.value)}
                  className={`flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors touch-manipulation ${
                    isSelected ? 'bg-[#002975]/10 font-semibold text-[#002975]' : 'text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                  }`}
                >
                  {option.icon && <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600" aria-hidden="true">{option.icon}</span>}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.description && <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">{option.description}</span>}
                  </span>
                  {isSelected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>,
        portalContainer ?? document.body,
      )}
    </>
  );
}
